/**
 * Subscription Checkout (Shopping Cart) Page
 * Displays subscription details, payment summary, T&C, and Pay Now before redirecting to Paymennt.
 * Also handles return from payment gateway - shows error on failure, redirects to success page on completion.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, Row, Col, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../services/AxiosInstance';
import { sortSubscriptionPlansBySortOrder } from '../../utils/subscriptionPlansSort';
import { getCheckoutIdFromSearchParams } from '../../utils/paymentCheckoutParams';
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './SubscriptionCheckout.css';
import visaCardLogo from '../../icons/cards/visa.png';
import masterCardLogo from '../../icons/cards/master card.png';

const VALID_CHECKOUT_INTENTS = new Set(['new', 'renewal', 'upgrade', 'downgrade', 'plan_change']);

function normalizeCheckoutIntent(raw) {
  if (raw && VALID_CHECKOUT_INTENTS.has(raw)) return raw;
  return 'new';
}

const CHECKOUT_INTENT_LABELS = {
  new: 'New plan',
  renewal: 'Renewal',
  upgrade: 'Upgrade',
  downgrade: 'Plan change',
  plan_change: 'Plan change',
};

function getSavedCheckoutState() {
  try {
    const saved = localStorage.getItem('subscription_checkout_plan');
    if (!saved) return null;
    return JSON.parse(saved);
  } catch {
    return null;
  }
}

/** e.g. Flowdo Task (Premium) */
function formatFlowdoSubscriptionLabel(plan) {
  if (!plan) return 'Subscription';
  const moduleRaw = (plan.Module?.display_name || plan.Module?.name || '').trim();
  const moduleTitle = moduleRaw
    ? moduleRaw.charAt(0).toUpperCase() + moduleRaw.slice(1).toLowerCase()
    : '';
  const planName = (plan.display_name || plan.PlanName || plan.name || '').trim();
  if (moduleTitle && planName) {
    return `Flowdo ${moduleTitle} (${planName})`;
  }
  if (planName) return `Flowdo (${planName})`;
  return 'Flowdo subscription';
}

const SubscriptionCheckout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const savedState = getSavedCheckoutState();
  const planIdFromUrl = searchParams.get('planId');
  const checkoutIntentParam = searchParams.get('checkoutIntent');
  const checkoutIntent = normalizeCheckoutIntent(checkoutIntentParam || savedState?.checkoutIntent);
  const billingCycleParam = searchParams.get('billingCycle') || 'monthly';
  const [billingCycle, setBillingCycle] = useState(billingCycleParam);
  useEffect(() => {
    setBillingCycle(billingCycleParam);
  }, [billingCycleParam]);
  const checkoutIdParam =
    getCheckoutIdFromSearchParams(searchParams) ||
    sessionStorage.getItem('paymennt_checkout_id');

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [checkingReturn, setCheckingReturn] = useState(false);
  const [pricingPreview, setPricingPreview] = useState(null);
  const [pricingPreviewLoading, setPricingPreviewLoading] = useState(false);
  const [pricingPreviewError, setPricingPreviewError] = useState(null);

  const STORAGE_KEY = 'subscription_checkout_plan';

  const planId =
    planIdFromUrl ||
    (savedState?.planId != null ? String(savedState.planId) : null);

  // Restore billingCycle from localStorage when using stored planId
  useEffect(() => {
    if (!planIdFromUrl && planId) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const { billingCycle: savedCycle } = JSON.parse(saved);
          if (savedCycle) setBillingCycle(savedCycle);
        }
      } catch (e) {}
    }
  }, [planIdFromUrl, planId]);

  // Fetch plan details - always try when we have planId (from URL or sessionStorage)
  useEffect(() => {
    const fetchPlan = async () => {
      if (!planId && !checkoutIdParam) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        if (planId) {
          const response = await axiosInstance.get('/subscription/plans');
          const plans = sortSubscriptionPlansBySortOrder(response.data?.data || response.data || []);
          const found = plans.find((p) => p.id == planId || p.planId == planId);
          setPlan(found || null);
          if (found) {
            const cycleToSave = planIdFromUrl
              ? (billingCycleParam || 'monthly')
              : (() => {
                  try {
                    const s = localStorage.getItem(STORAGE_KEY);
                    return s ? JSON.parse(s).billingCycle || 'monthly' : 'monthly';
                  } catch (e) {
                    return 'monthly';
                  }
                })();
            localStorage.setItem(
              STORAGE_KEY,
              JSON.stringify({
                planId: found.id || found.planId,
                billingCycle: cycleToSave,
                checkoutIntent: normalizeCheckoutIntent(
                  checkoutIntentParam || getSavedCheckoutState()?.checkoutIntent
                ),
              })
            );
          }
        }
      } catch (err) {
        console.error('Error fetching plan:', err);
        toast.error('Failed to load plan details');
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [planId, checkoutIntentParam]);

  // Server-side proration / amount (must match payment gateway)
  useEffect(() => {
    const id = plan?.id ?? plan?.planId;
    if (!id) {
      setPricingPreview(null);
      setPricingPreviewError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setPricingPreviewLoading(true);
      setPricingPreviewError(null);
      try {
        const res = await axiosInstance.get('/payment/preview-checkout', {
          params: {
            subscription_plan_id: id,
            billing_cycle: billingCycle,
            checkout_intent: checkoutIntent,
            user_count: 1,
          },
        });
        if (cancelled) return;
        if (res.data?.success) {
          setPricingPreview(res.data);
          setPricingPreviewError(null);
        } else {
          setPricingPreview(null);
          setPricingPreviewError(res.data?.message || 'Failed to load pricing');
        }
      } catch (e) {
        if (cancelled) return;
        setPricingPreview(null);
        if (['upgrade', 'plan_change', 'downgrade'].includes(checkoutIntent)) {
          setPricingPreviewError(e.response?.data?.message || 'Could not load prorated pricing. Please try again.');
        } else {
          setPricingPreviewError(null);
        }
      } finally {
        if (!cancelled) setPricingPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan?.id, plan?.planId, billingCycle, checkoutIntent]);

  // Handle return from Paymennt - poll status when we have checkoutId (from URL or sessionStorage)
  useEffect(() => {
    if (!checkoutIdParam) return;

    const checkStatus = async () => {
      setCheckingReturn(true);
      setPaymentError(null);
      try {
        const response = await axiosInstance.get(`/payment/status/${checkoutIdParam}`);
        const { status, failureReason } = response.data;

        if (status === 'completed' || status === 'paid') {
          localStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem('paymennt_checkout_id');
          navigate(
            `/subscription/payment-success?checkout=${encodeURIComponent(checkoutIdParam)}`,
            { replace: true }
          );
          return;
        }

        if (status === 'failed' || status === 'cancelled' || status === 'refunded') {
          sessionStorage.removeItem('paymennt_checkout_id');
          setPaymentError(
            failureReason || 'Unspecified/Unknown error occurred'
          );
          // Restore plan from localStorage for retry
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            try {
              const { planId: savedPlanId, billingCycle: savedCycle } = JSON.parse(saved);
              if (savedPlanId && !plan) {
                const res = await axiosInstance.get('/subscription/plans');
                const plans = sortSubscriptionPlansBySortOrder(res.data?.data || res.data || []);
                const found = plans.find((p) => p.id == savedPlanId || p.planId == savedPlanId);
                setPlan(found || null);
              }
              if (savedCycle) setBillingCycle(savedCycle);
            } catch (e) {}
          }
        } else {
          // Still pending - poll again
          setTimeout(() => checkStatus(), 3000);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        setPaymentError('We are verifying your payment. Please wait or try again.');
        setTimeout(() => checkStatus(), 5000);
      } finally {
        setCheckingReturn(false);
      }
    };

    checkStatus();
  }, [checkoutIdParam, navigate]);

  // After redirect to the gateway, the browser back button restores this page from bfcache
  // with React state frozen — isPaying stays true so "Pay Now" stays stuck on loading.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        setIsPaying(false);
        setCheckingReturn(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Restore plan from localStorage when returning from payment (no planId in URL)
  useEffect(() => {
    if (!planId && checkoutIdParam && !plan && !loading) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const { planId: savedPlanId, billingCycle: savedCycle } = JSON.parse(saved);
          if (savedPlanId) {
            axiosInstance.get('/subscription/plans').then((res) => {
              const plans = sortSubscriptionPlansBySortOrder(res.data?.data || res.data || []);
              const found = plans.find((p) => p.id == savedPlanId || p.planId == savedPlanId);
              setPlan(found || null);
            });
          }
          if (savedCycle) setBillingCycle(savedCycle);
        } catch (e) {}
      }
    }
  }, [planId, checkoutIdParam, plan, loading]);

  const listPrice = plan
    ? parseFloat(billingCycle === 'yearly' ? plan.yearly_price || 0 : plan.monthly_price || 0)
    : 0;
  const amount =
    pricingPreview != null
      ? Number(pricingPreview.amount)
      : pricingPreviewError && ['upgrade', 'plan_change', 'downgrade'].includes(checkoutIntent)
        ? 0
        : listPrice;
  const tax = 0;
  const serviceCharge = 0;
  const total = amount + tax + serviceCharge;
  const periodLabel =
    pricingPreview?.period_label || (billingCycle === 'yearly' ? '1 year' : '1 month');
  const pr = pricingPreview?.proration;

  const handlePayNow = async () => {
    if (!termsAccepted) {
      toast.error('Please confirm that you have read the Terms & Conditions');
      return;
    }
    if (!plan) {
      toast.error('Please select a subscription plan first');
      return;
    }
    if (pricingPreviewLoading) {
      toast.error('Please wait for pricing to load.');
      return;
    }
    if (amount <= 0) {
      toast.error('Invalid amount');
      return;
    }

    setIsPaying(true);
    setPaymentError(null);
    try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ planId: plan.id || plan.planId, billingCycle, checkoutIntent })
    );
      const response = await axiosInstance.post('/payment/create-checkout', {
        subscription_plan_id: plan.id || plan.planId,
        user_count: 1,
        billing_cycle: billingCycle,
        checkout_intent: checkoutIntent || 'new',
      });

      if (response.data?.success && response.data?.redirectUrl) {
        sessionStorage.setItem('paymennt_checkout_id', response.data.checkoutId || '');
        window.location.href = response.data.redirectUrl;
      } else {
        setPaymentError(response.data?.message || 'Failed to create checkout');
        setIsPaying(false);
      }
    } catch (error) {
      setPaymentError(
        error.response?.data?.message || 'Failed to start payment. Please try again.'
      );
      setIsPaying(false);
    }
  };

  const handleBackToPlans = () => {
    localStorage.removeItem(STORAGE_KEY);
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/user-subscription');
    }
  };

  if (loading && !plan && !checkoutIdParam) {
    return (
      <>
        <Row style={{ marginBottom: '0px !important' }}>
          <Col lg={12}>
            <Card className="user-list-card">
              <Card.Body style={{ minHeight: '400px', padding: '1.5rem' }}>
                <div className="checkout-loading">
                  <Spinner animation="border" style={{ color: '#C42126' }} />
                  <p className="mt-3 text-muted">Loading checkout...</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <InternalFooter />
      </>
    );
  }

  if (!plan && !checkoutIdParam) {
    return (
      <>
        <button
          type="button"
          className="checkout-back-button"
          onClick={handleBackToPlans}
        >
          <FiArrowLeft style={{ marginRight: '0.5rem' }} />
          Back
        </button>
        <Row style={{ marginBottom: '0px !important' }}>
          <Col lg={12}>
            <Card className="user-list-card">
              <Card.Body className="text-center p-5">
                <Alert variant="warning" className="checkout-alert">
                  No plan selected. Please select a subscription plan first.
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <InternalFooter />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className="checkout-back-button"
        onClick={handleBackToPlans}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <Row style={{ marginBottom: '0px !important' }}>
        <ToastContainer position="top-right" autoClose={5000} />
        <Col lg={12}>
          <Card className="user-list-card">
            <Card.Body style={{ minHeight: '400px', overflow: 'visible', padding: '1.5rem' }}>

              {paymentError && (
                <Alert variant="danger" className="checkout-alert mb-4" dismissible onClose={() => setPaymentError(null)}>
                  <strong>We are sorry that we could not process the payment due to the following error. Pease try again</strong>
                  <p className="mb-0 mt-2 fw-bold">{paymentError || 'Unknown error occurred'}</p>
                </Alert>
              )}

              {pricingPreviewError && (
                <Alert variant="warning" className="checkout-alert mb-4">
                  {pricingPreviewError}
                </Alert>
              )}

              <Row className="align-items-start">
                {/* Subscription Details */}
                <Col lg={6} className="mb-4">
                  <Card className="checkout-detail-card">
                    <Card.Header className="checkout-card-header checkout-detail-card-header">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <h5 className="mb-0">Subscription Details</h5>
                        <Badge pill bg="light" text="dark" className="checkout-intent-badge border">
                          {CHECKOUT_INTENT_LABELS[checkoutIntent]}
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="checkout-detail-rows">
                        <div className="checkout-detail-row">
                          <span className="text-muted">Subscription</span>
                          <span className="fw-semibold checkout-detail-value">
                            {formatFlowdoSubscriptionLabel(plan)}
                          </span>
                        </div>
                        <div className="checkout-detail-row">
                          <span className="text-muted">Period</span>
                          <span className="fw-semibold checkout-detail-value">{periodLabel}</span>
                        </div>
                        {pr && (
                          <>
                            <div className="checkout-detail-row">
                              <span className="text-muted">Days remaining (current period)</span>
                              <span className="fw-semibold checkout-detail-value">
                                {Math.round(Number(pr.remaining_days))}
                              </span>
                            </div>
                            <div className="checkout-detail-row">
                              <span className="text-muted">Credit from current plan</span>
                              <span className="fw-semibold checkout-detail-value">
                                −${Number(pr.old_credit).toFixed(2)}
                              </span>
                            </div>
                            <div className="checkout-detail-row">
                              <span className="text-muted">New plan (prorated)</span>
                              <span className="fw-semibold checkout-detail-value">
                                ${Number(pr.new_prorated).toFixed(2)}
                              </span>
                            </div>
                            <div className="checkout-detail-row small text-muted">
                              <span>List price ({billingCycle === 'yearly' ? 'yearly' : 'monthly'} full period)</span>
                              <span>${Number(pr.new_full_price).toFixed(2)}</span>
                            </div>
                          </>
                        )}
                        {/* <div className="checkout-detail-row">
                          <span className="text-muted">{pr ? 'Amount due today (USD)' : 'Amount (USD)'}</span>
                          <span className="fw-semibold checkout-detail-value">
                            {pricingPreviewLoading ? '…' : `$${amount.toFixed(2)}`}
                          </span>
                        </div> */}
                        <div className="checkout-detail-row checkout-detail-row-total">
                          <span className="fw-bold">Totals</span>
                          <span className="fw-bold checkout-detail-value">
                            {pricingPreviewLoading ? '…' : `$${total.toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                      <div className="checkout-renewal-notice mt-4">
                        <i className="fas fa-info-circle me-2"></i>
                        Your subscription will not be renewed automatically. System will send automated reminder prior to the expiry date.
                      </div>

                      <div className="payment-logos mt-4">
                        <img
                          src={visaCardLogo}
                          alt="Visa"
                          className="checkout-card-logo"
                        />
                        <img
                          src={masterCardLogo}
                          alt="Mastercard"
                          className="checkout-card-logo"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Payment Summary */}
                <Col lg={6} className="mb-4">
                  <Card className="checkout-payment-card">
                    <Card.Header className="checkout-card-header">
                      <h5 className="mb-0">Payment Summary</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="payment-summary-row">
                        <span className="text-muted">{pr ? 'Amount due (prorated upgrade)' : 'Subscription charge'}</span>
                        <span>{pricingPreviewLoading ? '…' : `$${amount.toFixed(2)} USD`}</span>
                      </div>
                      <div className="payment-summary-row">
                        <span className="text-muted">Tax</span>
                        <span>${tax.toFixed(2)} USD</span>
                      </div>
                      <div className="payment-summary-row">
                        <span className="text-muted">Service Charge</span>
                        <span>${serviceCharge.toFixed(2)} USD</span>
                      </div>
                      <div className="payment-summary-row payment-total">
                        <span className="fw-bold">Totals</span>
                        <span className="fw-bold">{pricingPreviewLoading ? '…' : `$${total.toFixed(2)} USD`}</span>
                      </div>

                      <Form.Group className="mb-3 mt-4">
                        <Form.Check
                          type="checkbox"
                          id="terms-check"
                          label={
                            <>
                              I confirm that I have read the{' '}
                              <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: '#C42126' }}>
                                Terms &amp; Conditions
                              </Link>
                              {' '}and{' '}
                              <Link to="/refund-cancellation-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#C42126' }}>
                                Refund &amp; Cancellation Policy
                              </Link>
                            </>
                          }
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          className="checkout-terms-check"
                        />
                      </Form.Group>

                      <button
                        type="button"
                        className="new-user-btn w-100 pay-now-btn checkout-pay-btn"
                        onClick={handlePayNow}
                        disabled={
                          !termsAccepted ||
                          isPaying ||
                          checkingReturn ||
                          pricingPreviewLoading ||
                          !!pricingPreviewError
                        }
                        style={{
                          border: 'none',
                          cursor:
                            !termsAccepted ||
                            isPaying ||
                            checkingReturn ||
                            pricingPreviewLoading ||
                            pricingPreviewError
                              ? 'not-allowed'
                              : 'pointer',
                          opacity:
                            !termsAccepted || isPaying || checkingReturn || pricingPreviewLoading || pricingPreviewError
                              ? 0.7
                              : 1,
                        }}
                      >
                        {isPaying ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Redirecting to payment...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-credit-card me-2"></i>
                            Pay Now
                          </>
                        )}
                      </button>

                      <p className="checkout-redirect-msg mt-3 mb-2">
                        You will be redirected to the Payment Gateway portal for making the payment using Credit/Debit card.
                      </p>
                      <p className="checkout-statement-msg text-muted small">
                        The payment will reflect in your card statement as &quot;Innate Technologies LLC-FZ&quot;, which is the parent company of flowdo.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <InternalFooter />
    </>
  );
};

export default SubscriptionCheckout;
